import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Flag, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AdminSignInForm } from '../components/admin/AdminSignInForm';
import { useAdminAccess } from '../components/admin/AdminAccessProvider';
import { ProShopCourseLogin } from '../components/proshop/ProShopCourseLogin';
import { proshopApi, proshopError } from '../components/proshop/proshopApi';
import { clearEventReturn, consumeEventReturn, rememberEventReturn } from '../components/events/eventReturn';
import '../components/proshop/proshop.css';

export default function ProShopLoginPage({ user, authLoading, onLogout, login, recovery }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const mode = params.get('mode') === 'admin' ? 'admin' : 'course';
  const admin = useAdminAccess();
  const [adminBusy, setAdminBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [unregistered, setUnregistered] = useState(false);
  const operation = useRef(0);
  const autoStarted = useRef(false);
  useEffect(() => () => { operation.current += 1; autoStarted.current = false; }, []);
  const openCourse = async () => {
    const id = ++operation.current;
    setBusy(true); setError(''); setUnregistered(false);
    try {
      const access = await proshopApi.access();
      if (operation.current !== id) return;
      const intended = consumeEventReturn();
      if (!access || typeof access.proshop_access !== 'boolean') throw new Error('Unable to verify course access.');
      navigate(intended?.startsWith('/proshop/') ? intended : '/proshop/dashboard', { replace: true });
    } catch (err) { if (operation.current === id) setError(proshopError(err)); }
    finally { if (operation.current === id) setBusy(false); }
  };
  useEffect(() => {
    if (params.get('continue') === '1' && mode === 'course' && user && !authLoading && !autoStarted.current) {
      autoStarted.current = true; openCourse();
    }
  });
  const blocked = busy || login.loading || recovery.loading || adminBusy;
  const register = () => {
    if (user) { clearEventReturn(); navigate('/proshop/register'); }
    else { rememberEventReturn('/proshop/register'); navigate('/?auth=signup'); }
  };
  const back = () => { operation.current += 1; clearEventReturn(); navigate('/book'); };
  return <main className="proshop-entry" data-testid="proshop-login-page">
    <div className="proshop-entry-inner">
      <header className="proshop-entry-brand"><img src="/images/par-seeker-logo-full.png" alt="Par Seeker" data-testid="proshop-login-logo" /><div><p className="course-subtle">PAR SEEKER</p><h1 data-testid="proshop-login-title">Pro Shop</h1></div></header>
      <section className="proshop-entry-panel" aria-label="Pro Shop access" data-testid="proshop-login-panel">
        <Tabs value={mode} onValueChange={value => { login.setPassword(''); recovery.setShow(false); setError(''); setParams(value === 'admin' ? { mode: 'admin' } : {}, { replace: true }); }}>
          <TabsList className="proshop-entry-tabs" data-testid="proshop-login-modes">
            <TabsTrigger value="course" disabled={blocked} data-testid="proshop-toggle-course"><Flag size={16} aria-hidden="true" /><span>Golf course</span></TabsTrigger>
            <TabsTrigger value="admin" disabled={blocked} data-testid="proshop-toggle-admin"><ShieldCheck size={16} aria-hidden="true" /><span>Par Seeker admin</span></TabsTrigger>
          </TabsList>
          <TabsContent value="course" className="proshop-entry-content" data-testid="proshop-course-login-panel">
            <ProShopCourseLogin user={user} authLoading={authLoading} login={login} recovery={recovery} busy={busy} onContinue={openCourse} onLogout={async () => { setBusy(true); setError(''); try { await onLogout(); setUnregistered(false); } catch { setError('Unable to sign out. Please retry.'); } finally { setBusy(false); } }} />
            {error && <p role="alert" className="course-error" data-testid="proshop-entry-error">{error}</p>}
            {unregistered && <p role="status" className="course-notice" data-testid="proshop-no-course">No course application is linked to this account yet.</p>}
          </TabsContent>
          <TabsContent value="admin" className="proshop-entry-content" data-testid="proshop-admin-login-panel">
            {admin.isAuthenticated ? <div className="space-y-5"><p className="break-all" data-testid="proshop-admin-signed-in-email">{admin.adminEmail}</p><Button className="course-primary w-full" onClick={() => navigate('/proshop/admin')} data-testid="proshop-admin-continue">Open Pro Shop administration</Button><Button variant="ghost" className="w-full" onClick={admin.logout} data-testid="proshop-admin-sign-out">Sign out</Button></div> : <AdminSignInForm onBusyChange={setAdminBusy} onSuccess={() => navigate('/proshop/admin', { replace: true })} />}
          </TabsContent>
        </Tabs>
        <div className="proshop-registration-options" data-testid="proshop-registration-options">
          <p className="course-subtle" data-testid="proshop-course-only-notice">Golf course access only</p>
          <h2 data-testid="proshop-access-question">Do you represent a golf course?</h2>
          <div className="proshop-registration-actions"><Button variant="outline" disabled={blocked || authLoading} onClick={register} data-testid="proshop-access-yes">Yes, register my course</Button><Button variant="ghost" disabled={blocked} onClick={back} data-testid="proshop-access-no">No, back to Bookings</Button></div>
        </div>
      </section>
      <Button variant="ghost" disabled={blocked} onClick={back} className="proshop-entry-back" data-testid="proshop-login-back"><ArrowLeft size={16} className="mr-2" />Back to Bookings</Button>
    </div>
  </main>;
}